import React from "react";
import { api, formatPHP, gcashQrUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Sun, Moon, Sparkles, CheckCircle2, ArrowLeft, Tag } from "lucide-react";
import { format } from "date-fns";

const TYPE_META = {
  day_use: { label: "Day Use", icon: Sun },
  night_use: { label: "Night Use", icon: Moon },
  whole_day: { label: "Overnight Use", icon: Sparkles },
};

// Compute which booking types are available for a given list of bookings on the date.
export function availableTypesFor(bookingsForDate) {
  const taken = new Set((bookingsForDate || []).map((b) => b.booking_type));
  if (taken.has("whole_day")) return [];
  if (taken.has("day_use") && taken.has("night_use")) return [];
  const all = ["day_use", "night_use", "whole_day"];
  return all.filter((t) => {
    if (t === "whole_day") return taken.size === 0;
    return !taken.has(t);
  });
}

// Day Use and Night Use are only available Mon-Thu (weekday 1-4 in JS local time).
// Fri/Sat/Sun are restricted to Overnight Use only.
function isWeekdayMonThu(d) {
  // JS getDay(): 0=Sun, 1=Mon, ... 6=Sat
  const dow = d.getDay();
  return dow >= 1 && dow <= 4;
}

// Compute which booking types are available across a multi-day range (intersection).
// Coexistence rule: Overnight blocks the whole date; Day + Night can coexist.
// Day/Night Use are Mon-Thu only.
export function availableTypesForRange(date, numDays, bookingsByDate) {
  if (!date) return [];
  let types = ["day_use", "night_use", "whole_day"];
  for (let i = 0; i < numDays; i++) {
    const d = new Date(date);
    d.setDate(d.getDate() + i);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const onDay = (bookingsByDate?.[iso] || []).filter((b) =>
      ["pending", "confirmed"].includes(b.status)
    );
    const taken = new Set(onDay.map((b) => b.booking_type));
    if (taken.has("whole_day")) return [];
    if (onDay.length > 0) types = types.filter((t) => t !== "whole_day");
    types = types.filter((t) => !taken.has(t));
    if (!isWeekdayMonThu(d)) types = types.filter((t) => t === "whole_day");
    if (types.length === 0) return [];
  }
  return types;
}

// A promo applies when ANY night of the stay falls within its date range.
export function promoForRange(promos, date, numDays) {
  if (!date) return null;
  for (const p of promos || []) {
    if (!p.active) continue;
    for (let i = 0; i < numDays; i++) {
      const d = new Date(date);
      d.setDate(d.getDate() + i);
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (iso >= p.start_date && iso <= p.end_date) return p;
    }
  }
  return null;
}

export default function BookingDialog({
  open,
  onOpenChange,
  date,
  bookingsForDate = [],
  bookingsByDate = {},
  rates = [],
  promos = [],
  addonServices = [],
  settings = {},
  ownerMode = false,
  onBooked,
}) {
  const [numDays, setNumDays] = React.useState(1);
  const available = React.useMemo(
    () => availableTypesForRange(date, numDays, bookingsByDate),
    [date, numDays, bookingsByDate]
  );

  const [step, setStep] = React.useState(1); // 1=details, 2=payment, 3=success
  const [bookingType, setBookingType] = React.useState(available[0] || null);
  const [form, setForm] = React.useState({
    guest_name: "",
    guest_email: "",
    guest_phone: "",
    num_guests: 2,
    special_requests: "",
  });
  const [proofFile, setProofFile] = React.useState(null);
  const [gcashRef, setGcashRef] = React.useState("");
  const [selectedAddons, setSelectedAddons] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);

  // Owner-block mode is controlled by the parent (only enabled when admin is logged in
  // and explicitly toggles it on the schedule page). Backend re-validates via admin JWT.
  const isOwner = !!ownerMode;

  // Reset state ONLY when dialog opens (not when availability refetches mid-flow)
  const wasOpenRef = React.useRef(false);
  React.useEffect(() => {
    if (open && !wasOpenRef.current) {
      setNumDays(1);
      const initialAvail = availableTypesForRange(date, 1, bookingsByDate);
      setStep(initialAvail.length === 0 ? 0 : 1);
      setBookingType(
        initialAvail.includes("whole_day") ? "whole_day" : initialAvail[0] || null
      );
      setForm({
        guest_name: "",
        guest_email: "",
        guest_phone: "",
        num_guests: 2,
        special_requests: "",
      });
      setProofFile(null);
      setGcashRef("");
      setSelectedAddons([]);
    }
    wasOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-pick a still-available type when range changes; owner mode prefers whole_day
  React.useEffect(() => {
    if (!open) return;
    if (available.length === 0) {
      setBookingType(null);
    } else if (isOwner && available.includes("whole_day")) {
      setBookingType("whole_day");
    } else if (!bookingType || !available.includes(bookingType)) {
      setBookingType(available.includes("whole_day") ? "whole_day" : available[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numDays, available.join(","), open, isOwner]);

  const selectedRate = rates.find((r) => r.type === bookingType);
  // Weekend pricing: Fri/Sat/Sun use rate.weekend_price when set (>0). Compute
  // the stay total by walking each night and summing per-night rates.
  const { total, perNight, hasWeekendNight } = React.useMemo(() => {
    if (!selectedRate || !date) {
      return { total: 0, perNight: [], hasWeekendNight: false };
    }
    const base = Number(selectedRate.price) || 0;
    const wknd = Number(selectedRate.weekend_price) || 0;
    let t = 0;
    let anyWeekend = false;
    const per = [];
    for (let i = 0; i < numDays; i++) {
      const d = new Date(date);
      d.setDate(d.getDate() + i);
      const dow = d.getDay(); // 0 Sun, 5 Fri, 6 Sat
      const isWknd = dow === 0 || dow === 5 || dow === 6;
      const p = isWknd && wknd > 0 ? wknd : base;
      if (isWknd && wknd > 0) anyWeekend = true;
      t += p;
      per.push({ date: d, price: p, isWeekend: isWknd && wknd > 0 });
    }
    return { total: t, perNight: per, hasWeekendNight: anyWeekend };
  }, [selectedRate, date, numDays]);
  const downPct = settings?.downpayment_pct || 50;
  // Promo: applies to Overnight Use only. If ANY night falls within an active
  // promo range, the whole booking is discounted.
  const promo = React.useMemo(
    () =>
      isOwner || bookingType !== "whole_day"
        ? null
        : promoForRange(promos, date, numDays),
    [promos, date, numDays, isOwner, bookingType]
  );
  const promoDiscount = promo
    ? promo.discount_type === "percent"
      ? Math.round(total * Number(promo.value)) / 100
      : Math.min(Number(promo.value) || 0, total)
    : 0;
  const finalTotal = Math.max(total - promoDiscount, 0);
  const down = Math.round((finalTotal * downPct) / 100);
  // Add-ons: flat = once · per_day = × days · per_guest = × guests. Paid at the villa,
  // so the downpayment above stays on the room rate only.
  const addonAmount = React.useCallback(
    (s) => {
      const unit = Number(s.price) || 0;
      if (s.addon_pricing === "per_day") return unit * numDays;
      if (s.addon_pricing === "per_guest") return unit * (Number(form.num_guests) || 1);
      return unit;
    },
    [numDays, form.num_guests]
  );
  const selectedAddonObjs = addonServices.filter((s) => selectedAddons.includes(s.id));
  const addonsTotal = selectedAddonObjs.reduce((sum, s) => sum + addonAmount(s), 0);
  const grandTotal = finalTotal + addonsTotal;
  const hasQr = !!settings?.gcash_qr_path;

  const endDate = React.useMemo(() => {
    if (!date) return null;
    const d = new Date(date);
    d.setDate(d.getDate() + numDays - 1);
    return d;
  }, [date, numDays]);

  const proceedToPayment = () => {
    if (!bookingType) return toast.error("Pick a booking type");
    if (isOwner) {
      // Owner bypass — skip payment step, submit directly
      submitOwner();
      return;
    }
    if (!form.guest_name) return toast.error("Please enter your name");
    if (!form.guest_phone) {
      return toast.error("Please enter your phone number");
    }
    setStep(2);
  };

  const submitOwner = async () => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("guest_name", form.guest_name || "Owner Block");
      fd.append("guest_email", "");
      fd.append("guest_phone", "");
      fd.append("booking_type", bookingType);
      fd.append("booking_date", format(date, "yyyy-MM-dd"));
      fd.append("num_days", String(numDays));
      fd.append("num_guests", "1");
      fd.append("special_requests", "");
      fd.append("gcash_reference", "");
      fd.append("owner_block", "1");
      await api.post("/bookings", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStep(3);
      onBooked && onBooked();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async () => {
    if (!proofFile) return toast.error("Please upload your GCash payment screenshot");
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("guest_name", form.guest_name);
      fd.append("guest_email", form.guest_email);
      fd.append("guest_phone", form.guest_phone);
      fd.append("booking_type", bookingType);
      fd.append("booking_date", format(date, "yyyy-MM-dd"));
      fd.append("num_days", String(numDays));
      fd.append("num_guests", String(form.num_guests || 1));
      fd.append("special_requests", form.special_requests || "");
      fd.append("gcash_reference", gcashRef);
      fd.append("addon_ids", selectedAddons.join(","));
      fd.append("payment_proof", proofFile);
      await api.post("/bookings", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStep(3);
      onBooked && onBooked();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="schedule-booking-dialog"
        className="max-w-2xl bg-[#0A0B10] border-[var(--border)] max-h-[92vh] overflow-y-auto p-4 sm:p-6"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl sm:text-3xl">
            {date ? format(date, "EEEE, MMMM d, yyyy") : ""}
          </DialogTitle>
          <DialogDescription className="text-[#A1A4B5] text-sm">
            {available.length === 0
              ? numDays > 1
                ? `No booking type is available across this ${numDays}-day range.`
                : "This date is fully booked."
              : step === 1
              ? isOwner
                ? "Owner mode — just pick the length of stay and we'll block this date."
                : "Choose how many days, your booking type, and tell us about your stay."
              : step === 2
              ? `Send ${formatPHP(down)} via GCash, then upload your receipt to confirm.`
              : "Reservation received."}
          </DialogDescription>
        </DialogHeader>

        {/* Step 0: nothing available */}
        {available.length === 0 && (
          <div className="py-6 text-center text-sm text-[#A1A4B5]">
            <p>This date is already booked. Please pick another date.</p>
          </div>
        )}

        {/* Step 1: details */}
        {step === 1 && available.length > 0 && (
          <div className="space-y-5">
            {!isOwner && promo && (
              <div
                className="rounded-xl border border-emerald-400/40 bg-emerald-400/10 p-3 flex items-center gap-2.5"
                data-testid="dialog-promo-banner"
              >
                <Tag size={16} className="text-emerald-300 flex-shrink-0" />
                <p className="text-emerald-200 text-xs leading-relaxed">
                  <span className="font-semibold uppercase tracking-[0.14em]">{promo.name}</span>
                  {" — "}
                  {promo.discount_type === "percent"
                    ? `${promo.value}%`
                    : formatPHP(promo.value)}{" "}
                  off applied automatically to your stay.
                </p>
              </div>
            )}
            {isOwner && (
              <div
                className="rounded-xl border border-[#C5A059]/40 bg-[#C5A059]/10 p-4 flex items-start gap-3"
                data-testid="owner-mode-banner"
              >
                <div className="text-[#C5A059] text-xl leading-none mt-0.5">✦</div>
                <div>
                  <div className="text-[#C5A059] text-xs uppercase tracking-[0.18em] font-semibold">
                    Owner Block Mode
                  </div>
                  <p className="text-[#E5E7EB] text-sm mt-1.5 leading-relaxed">
                    This will block the date instantly — no payment, no guest details required.
                    The date will appear as Overnight Use on the public calendar.
                  </p>
                </div>
              </div>
            )}
            {/* Multi-day picker */}
            <div className="rounded-xl border border-[var(--border)] bg-[#13151D] p-4">
              <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">
                Length of Stay
              </Label>
              <div className="flex items-center gap-3 mt-3">
                <button
                  type="button"
                  onClick={() => setNumDays((n) => Math.max(1, n - 1))}
                  className="w-9 h-9 rounded-full border border-[var(--border)] hover:border-[#C5A059] hover:text-[#C5A059] grid place-items-center transition-colors"
                  data-testid="dialog-days-decrement"
                  aria-label="Decrease days"
                >
                  −
                </button>
                <Input
                  type="number"
                  min="1"
                  max="30"
                  value={numDays}
                  onChange={(e) => setNumDays(Math.max(1, Math.min(30, Number(e.target.value) || 1)))}
                  className="w-24 text-center bg-[#0A0B10] border-[var(--border)]"
                  data-testid="dialog-num-days"
                />
                <span className="text-sm text-[#A1A4B5]">{numDays === 1 ? "day" : "days"}</span>
                <button
                  type="button"
                  onClick={() => setNumDays((n) => Math.min(30, n + 1))}
                  className="w-9 h-9 rounded-full border border-[var(--border)] hover:border-[#C5A059] hover:text-[#C5A059] grid place-items-center transition-colors"
                  data-testid="dialog-days-increment"
                  aria-label="Increase days"
                >
                  +
                </button>
              </div>
              {endDate && numDays > 1 && (
                <p className="text-[11px] text-[#A1A4B5] mt-3">
                  <span className="text-white">Check-in:</span> {format(date, "MMM d, yyyy")}{" "}
                  <span className="text-[#A1A4B5] mx-2">→</span>
                  <span className="text-white">Check-out:</span> {format(endDate, "MMM d, yyyy")}
                </p>
              )}
            </div>

            {!isOwner && (
              <div>
                <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">
                  Full Name
                </Label>
                <Input
                  data-testid="dialog-name"
                  value={form.guest_name}
                  onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                  className="mt-2 bg-[#13151D] border-[var(--border)]"
                  placeholder="Juan Dela Cruz"
                />
              </div>
            )}

            {!isOwner && (
              <div>
                <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">
                  Available Booking Types
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                  {available.map((t) => {
                    const meta = TYPE_META[t];
                    const Icon = meta.icon;
                    const r = rates.find((x) => x.type === t);
                    const active = bookingType === t;
                    return (
                      <button
                        key={t}
                        onClick={() => setBookingType(t)}
                        data-testid={`dialog-type-${t}`}
                        className={`relative p-4 rounded-xl border text-left transition-all ${
                          active
                            ? "border-[#C5A059] bg-[#C5A059]/10"
                            : "border-[var(--border)] hover:border-[#C5A059]/50"
                        }`}
                      >
                        <Icon
                          size={18}
                          className={active ? "text-[#C5A059]" : "text-[#A1A4B5]"}
                        />
                        <div className="mt-2 font-serif-display text-lg">{meta.label}</div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#A1A4B5] mt-0.5">
                          {r?.time_window || ""}
                        </div>
                        {(t === "day_use" || t === "night_use") && (
                          <div className="mt-1 inline-block text-[9px] uppercase tracking-[0.16em] text-[#C5A059] bg-[#C5A059]/10 border border-[#C5A059]/30 px-1.5 py-0.5 rounded">
                            Mon–Thu only
                          </div>
                        )}
                        <div className="mt-1 text-sm text-[#C5A059] font-medium" data-testid="dialog-type-price">
                          {r
                            ? (() => {
                                // For this card, show the price that applies to the
                                // SELECTED START date (so the user sees Fri = ₱12k, Mon = ₱10k etc.)
                                if (date && Number(r.weekend_price) > 0) {
                                  const dow = new Date(date).getDay();
                                  const isWknd = dow === 0 || dow === 5 || dow === 6;
                                  return formatPHP(isWknd ? r.weekend_price : r.price);
                                }
                                return formatPHP(r.price);
                              })()
                            : "—"}
                        </div>
                        {r && Number(r.weekend_price) > 0 && (
                          <div className="mt-1 text-[9px] uppercase tracking-[0.16em] text-[#A1A4B5]">
                            Mon–Thu {formatPHP(r.price)} · Fri–Sun {formatPHP(r.weekend_price)}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                {bookingsForDate.length > 0 && (
                  <p className="text-[11px] text-[#A1A4B5] mt-2">
                    Already booked:{" "}
                    {bookingsForDate
                      .map((b) => TYPE_META[b.booking_type]?.label || b.booking_type)
                      .join(", ")}
                  </p>
                )}
              </div>
            )}

            {!isOwner && (
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">
                    Email <span className="normal-case tracking-normal text-[#A1A4B5]/70">(optional)</span>
                  </Label>
                  <Input
                    data-testid="dialog-email"
                    type="email"
                    value={form.guest_email}
                    onChange={(e) => setForm({ ...form, guest_email: e.target.value })}
                    className="mt-2 bg-[#13151D] border-[var(--border)]"
                    placeholder="you@example.com (optional)"
                  />
                  {!form.guest_email && (
                    <p
                      className="mt-2 text-xs text-[#C5A059] leading-relaxed"
                      data-testid="no-email-note"
                    >
                      No email? Just screenshot your GCash payment and message us on Facebook
                      {settings?.facebook ? (
                        <>
                          {" "}
                          →{" "}
                          <a
                            href={settings.facebook}
                            target="_blank"
                            rel="noreferrer"
                            className="underline hover:text-[#d4b270]"
                            data-testid="no-email-fb-link"
                          >
                            {settings.facebook.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                          </a>
                        </>
                      ) : (
                        <> — we'll confirm your booking there.</>
                      )}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Phone</Label>
                  <Input
                    data-testid="dialog-phone"
                    value={form.guest_phone}
                    onChange={(e) => setForm({ ...form, guest_phone: e.target.value })}
                    className="mt-2 bg-[#13151D] border-[var(--border)]"
                    placeholder="+63 917 …"
                  />
                </div>
                <div>
                  <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Guests</Label>
                  <Input
                    data-testid="dialog-guests"
                    type="number"
                    min="1"
                    max={settings?.max_guests || 20}
                    value={form.num_guests}
                    onChange={(e) => setForm({ ...form, num_guests: Number(e.target.value) })}
                    className="mt-2 bg-[#13151D] border-[var(--border)]"
                  />
                </div>
              </div>
            )}

            {!isOwner && (
              <div>
                <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">
                  Special Requests
                </Label>
                <Textarea
                  data-testid="dialog-requests"
                  value={form.special_requests}
                  onChange={(e) => setForm({ ...form, special_requests: e.target.value })}
                  className="mt-2 bg-[#13151D] border-[var(--border)]"
                  rows={2}
                />
              </div>
            )}

            {!isOwner && addonServices.length > 0 && (
              <div>
                <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">
                  Add-ons{" "}
                  <span className="normal-case tracking-normal text-[#A1A4B5]/70">
                    (optional — paid at the villa)
                  </span>
                </Label>
                <div className="grid sm:grid-cols-2 gap-2 mt-2" data-testid="dialog-addons-list">
                  {addonServices.map((s) => {
                    const checked = selectedAddons.includes(s.id);
                    const amt = addonAmount(s);
                    const modeNote =
                      s.addon_pricing === "per_day"
                        ? `${formatPHP(s.price)} × ${numDays} ${numDays === 1 ? "day" : "days"}`
                        : s.addon_pricing === "per_guest"
                        ? `${formatPHP(s.price)} × ${form.num_guests || 1} ${(form.num_guests || 1) === 1 ? "guest" : "guests"}`
                        : "per booking";
                    return (
                      <button
                        type="button"
                        key={s.id}
                        onClick={() =>
                          setSelectedAddons((prev) =>
                            checked ? prev.filter((id) => id !== s.id) : [...prev, s.id]
                          )
                        }
                        data-testid={`dialog-addon-${s.id}`}
                        className={`flex items-center justify-between gap-3 p-3 rounded-xl border text-left transition-all ${
                          checked
                            ? "border-[#C5A059] bg-[#C5A059]/10"
                            : "border-[var(--border)] hover:border-[#C5A059]/50"
                        }`}
                      >
                        <span className="flex items-center gap-2.5 min-w-0">
                          <span
                            className={`w-4 h-4 rounded border grid place-items-center flex-shrink-0 text-[10px] ${
                              checked
                                ? "border-[#C5A059] bg-[#C5A059] text-black"
                                : "border-[var(--border)]"
                            }`}
                          >
                            {checked ? "✓" : ""}
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm text-white truncate">{s.name}</span>
                            <span className="block text-[10px] text-[#A1A4B5]">{modeNote}</span>
                          </span>
                        </span>
                        <span className="text-sm text-[#C5A059] font-medium flex-shrink-0">
                          +{formatPHP(amt)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {!isOwner && (
              <div className="border-t border-[var(--border)] pt-4 space-y-1.5 text-sm" data-testid="dialog-pricing">
                {numDays === 1 ? (
                  <div className="flex justify-between text-[#A1A4B5]">
                    <span>
                      Rate ({perNight[0] ? format(perNight[0].date, "EEE") : ""})
                      {perNight[0]?.isWeekend && (
                        <span className="ml-1.5 text-[9px] uppercase tracking-[0.16em] text-[#C5A059] bg-[#C5A059]/10 border border-[#C5A059]/30 px-1.5 py-0.5 rounded">
                          Weekend
                        </span>
                      )}
                    </span>
                    <span className="text-white">{formatPHP(perNight[0]?.price || 0)}</span>
                  </div>
                ) : (
                  <>
                    {perNight.map((p, i) => (
                      <div key={i} className="flex justify-between text-[#A1A4B5] text-xs">
                        <span>
                          {format(p.date, "EEE, MMM d")}
                          {p.isWeekend && (
                            <span className="ml-1.5 text-[9px] uppercase tracking-[0.16em] text-[#C5A059]">
                              · weekend
                            </span>
                          )}
                        </span>
                        <span className="text-white">{formatPHP(p.price)}</span>
                      </div>
                    ))}
                  </>
                )}
                <div className="flex justify-between text-[#A1A4B5] border-t border-[var(--border)] pt-2 mt-2">
                  <span className="font-medium">Total ({numDays} {numDays === 1 ? "day" : "days"})</span>
                  {promo ? (
                    <span className="line-through" data-testid="dialog-original-total">
                      {formatPHP(total)}
                    </span>
                  ) : (
                    <span className="text-white font-medium">{formatPHP(total)}</span>
                  )}
                </div>
                {promo && (
                  <>
                    <div className="flex justify-between items-center" data-testid="dialog-promo-row">
                      <span className="flex items-center gap-1.5 text-emerald-300">
                        <Tag size={12} />
                        {promo.name}
                        <span className="text-[9px] uppercase tracking-[0.14em] bg-emerald-400/10 border border-emerald-400/30 px-1.5 py-0.5 rounded">
                          {promo.discount_type === "percent"
                            ? `${promo.value}% off`
                            : `${formatPHP(promo.value)} off`}
                        </span>
                      </span>
                      <span className="text-emerald-300">−{formatPHP(promoDiscount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-white font-medium">Promo Total</span>
                      <span className="text-[#C5A059] font-semibold" data-testid="dialog-promo-total">
                        {formatPHP(finalTotal)}
                      </span>
                    </div>
                  </>
                )}
                {selectedAddonObjs.length > 0 && (
                  <>
                    {selectedAddonObjs.map((s) => (
                      <div key={s.id} className="flex justify-between text-[#A1A4B5] text-xs" data-testid={`pricing-addon-${s.id}`}>
                        <span>{s.name}</span>
                        <span className="text-white">+{formatPHP(addonAmount(s))}</span>
                      </div>
                    ))}
                    <div className="flex justify-between border-t border-[var(--border)] pt-2 mt-2">
                      <span className="text-white font-medium">Grand Total</span>
                      <span className="text-white font-semibold" data-testid="dialog-grand-total">
                        {formatPHP(grandTotal)}
                      </span>
                    </div>
                  </>
                )}
                <div className="flex justify-between text-[#A1A4B5]">
                  <span>Downpayment ({downPct}%)</span>
                  <span className="text-[#C5A059] font-semibold">{formatPHP(down)}</span>
                </div>
                {selectedAddonObjs.length > 0 && (
                  <p className="text-[10px] text-[#A1A4B5] mt-1" data-testid="addons-payment-note">
                    Add-ons ({formatPHP(addonsTotal)}) are paid at the villa — your GCash downpayment
                    covers the villa rate only.
                  </p>
                )}
                {hasWeekendNight && (
                  <p className="text-[10px] text-[#A1A4B5] mt-2">
                    Weekend rate (Fri–Sun) applied automatically per night.
                  </p>
                )}
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <Button
                variant="ghost"
                onClick={() => onOpenChange(false)}
                className="text-[#A1A4B5] hover:text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={proceedToPayment}
                disabled={submitting}
                className="gold-btn rounded-full px-6"
                data-testid="dialog-proceed-button"
              >
                {isOwner
                  ? submitting
                    ? "Blocking…"
                    : "Block This Date"
                  : "Continue to Payment"}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: payment */}
        {step === 2 && (
          <div className="grid md:grid-cols-2 gap-5">
            <div className="bg-white p-3 rounded-xl flex items-center justify-center">
              {hasQr ? (
                <img
                  src={gcashQrUrl()}
                  alt="GCash QR"
                  className="w-full h-auto max-h-72 object-contain"
                  data-testid="dialog-qr-image"
                />
              ) : (
                <div className="text-center text-black p-4">
                  <div className="font-serif-display text-xl mb-2">GCash Payment</div>
                  <div className="text-xs">Account Name</div>
                  <div className="font-semibold text-sm">
                    {settings?.gcash_account_name}
                  </div>
                  <div className="text-xs mt-3">GCash Number</div>
                  <div className="font-semibold text-sm">{settings?.gcash_number}</div>
                </div>
              )}
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">
                  Account Name
                </Label>
                <div className="text-white mt-0.5">{settings?.gcash_account_name}</div>
              </div>
              <div>
                <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">
                  GCash Number
                </Label>
                <div className="text-[#C5A059] font-semibold mt-0.5">
                  {settings?.gcash_number}
                </div>
              </div>
              <div>
                <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">
                  Amount Due
                </Label>
                <div className="font-serif-display text-2xl text-[#C5A059] mt-0.5">
                  {formatPHP(down)}
                </div>
              </div>
              <div>
                <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">
                  Upload Payment Screenshot
                </Label>
                <Input
                  data-testid="dialog-proof-input"
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                  className="mt-2 bg-[#13151D] border-[var(--border)] cursor-pointer"
                />
                {proofFile && (
                  <p className="text-xs text-[#C5A059] mt-2">Selected: {proofFile.name}</p>
                )}
                <p className="text-xs text-[#A1A4B5] mt-2 leading-relaxed">
                  After your GCash transfer, just upload the screenshot here. Our admin will verify it and confirm your booking.
                </p>
              </div>
            </div>
            <div className="md:col-span-2 flex gap-3 justify-between pt-2">
              <Button
                variant="ghost"
                onClick={() => setStep(1)}
                className="text-[#A1A4B5] hover:text-white"
                data-testid="dialog-back-button"
              >
                <ArrowLeft size={14} className="mr-1" /> Back
              </Button>
              <Button
                onClick={submit}
                disabled={submitting}
                className="gold-btn rounded-full px-8"
                data-testid="dialog-submit-button"
              >
                {submitting ? "Submitting…" : "Confirm Booking"}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: success */}
        {step === 3 && (
          <div className="flex flex-col items-center py-6 text-center">
            <CheckCircle2 size={56} className="text-[#C5A059]" />
            <p className="text-[#A1A4B5] mt-4 text-sm leading-relaxed max-w-sm">
              {isOwner
                ? "Date blocked successfully. The villa is reserved for your personal use — no payment required."
                : "Salamat! We've received your booking and payment proof. Our concierge will verify your downpayment and reach out to confirm shortly."}
            </p>
            {!isOwner && !form.guest_email && (
              <p
                className="mt-3 text-xs text-[#C5A059] leading-relaxed max-w-sm"
                data-testid="success-no-email-note"
              >
                Since you didn't provide an email, please message us on Facebook
                {settings?.facebook ? (
                  <>
                    {" "}
                    →{" "}
                    <a
                      href={settings.facebook}
                      target="_blank"
                      rel="noreferrer"
                      className="underline hover:text-[#d4b270]"
                    >
                      {settings.facebook.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                    </a>
                  </>
                ) : (
                  ""
                )}{" "}
                with your screenshot so we can confirm your booking.
              </p>
            )}
            <Button
              onClick={() => onOpenChange(false)}
              className="gold-btn rounded-full px-8 mt-6"
              data-testid="dialog-close-button"
            >
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
